// Header.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Bell, ChevronDown, Hourglass, UserCircle } from 'lucide-react';
import { Menu } from '@headlessui/react';
import { formatBaseLocationWithCode, getBaseLocationCode } from './militaryOptions';

const DAY_MS = 24 * 60 * 60 * 1000;

function getTrialCountdown(subscription, nowMs) {
  const status = String(subscription?.subscriptionStatus || '').trim().toLowerCase();
  if (status !== 'trial' || !subscription?.trialEndsAt) return null;

  const endsAt = new Date(subscription.trialEndsAt);
  if (Number.isNaN(endsAt.getTime())) return null;

  const remainingMs = endsAt.getTime() - nowMs;
  const isExpired = remainingMs < 0;
  const daysLeft = Math.max(0, Math.ceil(remainingMs / DAY_MS));
  const endsLabel = endsAt.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  let label = `${daysLeft} trial days left`;
  if (daysLeft === 1) label = '1 trial day left';
  if (daysLeft === 0 && !isExpired) label = 'Trial ends today';
  if (isExpired) label = 'Trial expired';

  return {
    label,
    detail: isExpired ? 'Upgrade to restore access' : `Ends ${endsLabel}`,
    tone: isExpired ? 'expired' : (daysLeft <= 7 ? 'soon' : 'normal'),
    title: isExpired
      ? `Trial expired on ${endsLabel}. Upgrade to restore location access.`
      : `Trial access ends ${endsLabel}. Upgrade before then to keep location access active.`,
  };
}

function normalizeSubscriptionText(value) {
  return String(value || '').trim().toLowerCase();
}

export function isFullPremiumSubscription(subscription) {
  const status = normalizeSubscriptionText(subscription?.subscriptionStatus);
  const billingPlanKey = normalizeSubscriptionText(subscription?.billingPlanKey);
  const planNames = [
    subscription?.displayPlanName,
    subscription?.contractPlanName,
    subscription?.planName,
  ].map(normalizeSubscriptionText);

  const isPremiumPlan = (
    planNames.includes('premium') ||
    (billingPlanKey.startsWith('premium') && billingPlanKey !== 'premium_trial')
  );

  return isPremiumPlan && status === 'active';
}

const Header = ({
  user,
  selectedBase,
  setSelectedBase,
  locationTree = {},
  locationsLoading = false,
  locationsError = '',
  onRetryLocations,
  globalView,
  setGlobalView,
  handleLogout,
  activeTab,
  setActiveTab,
  subscription,
  alerts = [],
  clearAlerts,
  onOpenProfile
}) => {
  const count = alerts.length;
  const [nowMs, setNowMs] = useState(() => Date.now());
  const normalizedRole = String(user?.role || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
  const normalizedShop = String(user?.shop || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
  const canAccessOperationalData = user?.can_access_operational_data !== false;
  const isSupportUser = Boolean(user?.is_support_user);
  const supportLocations = user?.accessible_support_locations || [];
  const canViewAdmin = Boolean(
    (user?.is_superuser && canAccessOperationalData) ||
    ['siteadmin', 'admin', 'commander', 'management', 'production'].includes(normalizedRole) ||
    normalizedShop === 'production'
  );
  const timeFields = [
    'actMxShow', 'actCrewShow', 'actEngStrt', 'actTaxi',
    'actTakeoff', 'actLand', 'called', 'arrived',
    'fixed', 'estimatedCompletion'
  ];

  const alertIconMap = {
    time: String.fromCodePoint(0x23F0),
    edit: String.fromCodePoint(0x270F, 0xFE0F),
    warning: String.fromCodePoint(0x26A0, 0xFE0F),
    launch: String.fromCodePoint(0x1F680),
    delete: String.fromCodePoint(0x1F5D1, 0xFE0F),
    issue: String.fromCodePoint(0x1F195),
    swap: String.fromCodePoint(0x1F501),
    complete: String.fromCodePoint(0x2705),
  };

  function getAlertIcon(alert) {
    const action = String(alert?.action || '').toUpperCase();
    const message = String(alert?.message || alert?.details || '');

    if (action === 'ATO_SPARE_SWAP' || message.toLowerCase().includes('spare swap')) return alertIconMap.swap;
    if (action.includes('DELETE') || message.toLowerCase().includes('deleted')) return alertIconMap.delete;
    if (action.includes('COMPLIED') || message.toLowerCase().includes('complied')) return alertIconMap.complete;
    if (action.includes('ISSUE') || message.toLowerCase().includes('issue')) return alertIconMap.issue;
    if (action.includes('UPLOAD') || action.includes('ADDED') || message.toLowerCase().includes('new ')) return alertIconMap.launch;
    if (action.includes('WARNING') || message.toLowerCase().startsWith('warning')) return alertIconMap.warning;
    if (timeFields.includes(alert?.field)) return alertIconMap.time;
    return alertIconMap.edit;
  }

  function cleanAlertMessage(alert) {
    return String(alert?.message || alert?.details || '')
      .replace(/^(â\S*|Ã\S*|\p{Extended_Pictographic})\s*/u, '')
      .replace(/\s*(â†’|→)\s*/g, ': ')
      .trim();
  }

  // --- sound toggle & audio element ---
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem('alertSound') !== 'off');
  const audioRef = useRef(null);

  useEffect(() => {
    const interval = window.setInterval(() => setNowMs(Date.now()), 60 * 1000);
    return () => window.clearInterval(interval);
  }, []);
  

 // unlock autoplay after any user click (Chrome/Safari policy)
  useEffect(() => {
    const unlock = () => {
      const a = audioRef.current;
      if (!a) return;
      a.play().then(() => { a.pause(); a.currentTime = 0; }).catch(() => {});
      window.removeEventListener('click', unlock);
    };
    window.addEventListener('click', unlock, { once: true });
    return () => window.removeEventListener('click', unlock);
  }, []);

  // play when a NEW alert arrives
  const prevCount = useRef(alerts.length);
  useEffect(() => {
    if (soundOn && alerts.length > prevCount.current) {
      audioRef.current?.play().catch(() => {});
    }
    prevCount.current = alerts.length;
  }, [alerts, soundOn]);

  const trialCountdown = getTrialCountdown(subscription, nowMs);
  const showUpgradeButton = canAccessOperationalData && !isSupportUser && !isFullPremiumSubscription(subscription);
  const selectedBaseCode = getBaseLocationCode(selectedBase);
  const locationOptions = useMemo(() => {
    const options = isSupportUser
      ? supportLocations.map(base => ({ base, label: base }))
      : Object.entries(locationTree || {}).flatMap(([country, states]) =>
        Object.entries(states || {}).flatMap(([state, bases]) =>
          (bases || []).map(base => ({
            base,
            label: `${country} / ${state} / ${base}`,
          }))
        )
      );
    const seen = new Set();
    return options.filter(option => {
      const base = String(option.base || '').trim();
      if (!base || seen.has(base)) return false;
      seen.add(base);
      return true;
    });
  }, [isSupportUser, locationTree, supportLocations]);
  const [locationSearch, setLocationSearch] = useState('');
  const [locationMenuOpen, setLocationMenuOpen] = useState(false);
  const locationPickerRef = useRef(null);

  useEffect(() => {
    setLocationSearch(selectedBase || '');
  }, [selectedBase]);

  useEffect(() => {
    if (!locationMenuOpen) return undefined;
    const handlePointerDown = event => {
      if (!locationPickerRef.current?.contains(event.target)) {
        setLocationMenuOpen(false);
        setLocationSearch(selectedBase || '');
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [locationMenuOpen, selectedBase]);

  const filteredLocationOptions = useMemo(() => {
    const query = String(locationSearch || '').trim().toLowerCase();
    if (!query || query === String(selectedBase || '').trim().toLowerCase()) {
      return locationOptions;
    }
    return locationOptions.filter(option =>
      option.base.toLowerCase().includes(query) ||
      option.label.toLowerCase().includes(query) ||
      getBaseLocationCode(option.base).toLowerCase().includes(query)
    );
  }, [locationOptions, locationSearch, selectedBase]);

  const commitLocationOption = option => {
    if (!option?.base) return;
    if (option.base !== selectedBase) {
      setSelectedBase(option.base);
    }
    if (user.is_superuser && globalView) {
      setGlobalView(false);
    }
    setLocationSearch(option.base);
    setLocationMenuOpen(false);
  };

  const selectLocationInput = value => {
    setLocationSearch(value);
    setLocationMenuOpen(true);
    const normalized = String(value || '').trim().toLowerCase();
    const match = locationOptions.find(option =>
      option.base.toLowerCase() === normalized ||
      option.label.toLowerCase() === normalized
    );
    if (match) {
      commitLocationOption(match);
    }
  };

  const handleLocationKeyDown = event => {
    if (event.key === 'ArrowDown') {
      setLocationMenuOpen(true);
      event.preventDefault();
      return;
    }
    if (event.key === 'Enter') {
      const firstMatch = filteredLocationOptions[0];
      if (firstMatch) {
        commitLocationOption(firstMatch);
        event.preventDefault();
      }
      return;
    }
    if (event.key === 'Escape') {
      setLocationMenuOpen(false);
      setLocationSearch(selectedBase || '');
      event.preventDefault();
    }
  };
  
  const renderLocationSelector = () => (
    (user.is_superuser || isSupportUser) && canAccessOperationalData && (
      <div className="app-header__location">
        {/* Location Selector */}
        <div className="app-header__location-row">
          <label className="app-header__location-label">View Location:</label>
          <div className="app-header__location-combobox" ref={locationPickerRef}>
            <input
              type="text"
              value={locationSearch}
              onChange={e => selectLocationInput(e.target.value)}
              onFocus={e => {
                setLocationMenuOpen(true);
                e.target.select();
              }}
              onKeyDown={handleLocationKeyDown}
              placeholder={isSupportUser && !supportLocations.length ? 'No support locations granted' : 'Select or type a location'}
              className="app-header__select"
              aria-label="View Location"
              aria-expanded={locationMenuOpen}
              aria-controls="app-header-location-options"
              aria-autocomplete="list"
              role="combobox"
              disabled={isSupportUser && !supportLocations.length}
            />
            <button
              type="button"
              className="app-header__location-menu-button"
              onClick={() => setLocationMenuOpen(open => !open)}
              aria-label="Show locations"
              disabled={isSupportUser && !supportLocations.length}
            >
              <ChevronDown size={16} />
            </button>
            {locationMenuOpen ? (
              <div className="app-header__location-menu" id="app-header-location-options" role="listbox">
                {locationsLoading && !isSupportUser ? <div className="app-header__location-empty" role="status">Loading sites...</div> : null}
                {locationsError && !isSupportUser ? <div className="app-header__location-empty" role="alert">{locationsError} <button type="button" onClick={onRetryLocations}>Retry</button></div> : null}
                {filteredLocationOptions.length ? filteredLocationOptions.map(option => (
                  <button
                    type="button"
                    key={option.base}
                    className={`app-header__location-option${option.base === selectedBase ? ' app-header__location-option--active' : ''}`}
                    onMouseDown={event => event.preventDefault()}
                    onClick={() => commitLocationOption(option)}
                    role="option"
                    aria-selected={option.base === selectedBase}
                  >
                    <span>{formatBaseLocationWithCode(option.base)}</span>
                    {option.label !== option.base ? <small>{option.label}</small> : null}
                  </button>
                )) : (isSupportUser || (!locationsLoading && !locationsError)) ? (
                  <div className="app-header__location-empty">No matching locations</div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
        {user.is_superuser ? <div className="app-header__toggle">
          <span>Global View</span>
          <input
            type="checkbox"
            checked={globalView}
            onChange={e => setGlobalView(e.target.checked)}
          />
        </div> : null}
        {selectedBaseCode ? (
          <div className="app-header__base-code" title={`${selectedBase} base code`}>
            {selectedBaseCode}
          </div>
        ) : null}
      </div>
    )
  );
    
  return (
    <header className="app-header">
      <div className="app-header__bar">
        
        {/* App title */}
        <div className="app-header__brand">
          <h3 className="app-header__title">
            Flightline Operations Readiness &amp; Generation Engine <span className="app-header__title-acronym">(FORGE)</span>
          </h3>
        </div>
        
        {/* Right-side controls */}
        <div className="app-header__controls">
          <div className="app-header__identity">
            {/* Notifications bell */}
            <Menu as="div" className="app-header__alerts">
              <Menu.Button className="app-header__alert-button">
                <Bell size={24} />
                {alerts.length > 0 && (
                  <span className="app-header__alert-count">
                    {alerts.length}
                  </span>
                )}
              </Menu.Button>        
              <Menu.Items className="app-header__alert-menu">
                {/* header inside dropdown */}
                <div className="app-header__alert-menu-head">
                  <span>Alerts ({alerts.length})</span>
                  {alerts.length > 0 && (
                    <button
                      onClick={clearAlerts}
                      className="app-header__alert-clear"
                    >
                      Clear All
                    </button>
                  )}
                </div>

                {alerts.length > 0
                  ? alerts.map(n => {
                      function formatLocalTimestamp(isoString) {
                        if (!isoString) return '';
                        const dt = new Date(isoString);
                        return dt.toLocaleString([], {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        });
                      }
                
                      const isTimeField = timeFields.includes(n.field);
                      const showTime = isTimeField && n.new_value;
                
                      return (
                        <Menu.Item key={n.id}>
                          {({ active }) => (
                            <div className={`app-header__alert-item ${active ? 'is-active' : ''}`}>
                              <span className="app-header__alert-icon" aria-hidden="true">
                                {getAlertIcon(n)}
                              </span>
                              <span className="app-header__alert-text">
                                {cleanAlertMessage(n)}
                              </span>
                              {showTime && (
                                <span className="app-header__alert-time">
                                  &nbsp;({formatLocalTimestamp(n.new_value)})
                                </span>
                              )}
                            </div>
                          )}
                        </Menu.Item>
                      );
                    })
                  : (
                      <div className="app-header__alert-empty">
                        No new alerts
                      </div>
                    )
                }      
              </Menu.Items>
            </Menu>
            
            <audio ref={audioRef} src="/beep.m4a" preload="auto" />

            {/* User info */}
            <p className="app-header__user">
              Welcome, {user.email} | Role: {user.role} | Location: {user.location}
              {user.is_superuser && ' | SUPERUSER'}
              {isSupportUser && ' | MFE SUPPORT'}
            </p>
          </div>

          <div className="app-header__command">
            {renderLocationSelector()}
            <div className="app-header__account-actions">
              {trialCountdown && canAccessOperationalData && !isSupportUser && (
                <button
                  type="button"
                  onClick={() => setActiveTab?.('upgrade')}
                  className={`app-header__trial is-${trialCountdown.tone}`}
                  title={trialCountdown.title}
                  aria-label={trialCountdown.title}
                >
                  <Hourglass size={17} />
                  <span className="app-header__trial-text">
                    <span className="app-header__trial-label">{trialCountdown.label}</span>
                    <span className="app-header__trial-detail">{trialCountdown.detail}</span>
                  </span>
                </button>
              )}
              {user?.is_superuser && (
                <button
                  type="button"
                  onClick={() => setActiveTab?.('ownerPortal')}
                  className={`app-header__admin app-header__owner-portal ${activeTab === 'ownerPortal' ? 'is-active' : ''}`}
                >
                  MFE Owner Portal
                </button>
              )}
              {canViewAdmin && (
                <button
                  type="button"
                  onClick={() => setActiveTab?.('admin')}
                  className={`app-header__admin ${activeTab === 'admin' ? 'is-active' : ''}`}
                >
                  Admin
                </button>
              )}
              {showUpgradeButton && (
                <button
                  type="button"
                  onClick={() => setActiveTab?.('upgrade')}
                  className={`app-header__admin app-header__upgrade ${activeTab === 'upgrade' ? 'is-active' : ''}`}
                >
                  Upgrade
                </button>
              )}
              <button
                type="button"
                onClick={onOpenProfile}
                className="app-header__profile"
                title="Profile and preferences"
                aria-label="Profile and preferences"
                data-tour-target="profile-button"
              >
                <UserCircle size={18} />
                Profile
              </button>
              {/* Logout button */}
              <button
                onClick={handleLogout}
                className="app-header__logout"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
